import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import UserInfo from '../../../../interfaces/UserInfo';
import { useAuth } from '../../../../firebase/AuthProvider';
import AvatarImage from '../../../ui/Avatar/AvatarImage';
import { useNavigate } from 'react-router-dom';
import { createConversation } from '../../../../services/chatService/ChatService';
import { toast } from 'react-toastify';
import { Search, X, MessageCircle, Users } from 'lucide-react';
import { selectedConversation$ } from '../../../../services/Subjects/ChatSubjects';
import { currentConversationId$ } from '../../../../services/Subjects/userSubject';
import { registerChatEscapeLayer } from '../../../../utilities/chat/chatEscapeLayers';

interface ChatParticipantModalProps {
    showUsersModal: boolean;
    handleCloseUsersModal: () => void;
    participants: UserInfo[];
    isSideView?: boolean;
}

const ChatParticipantModal: React.FC<ChatParticipantModalProps> = ({
    handleCloseUsersModal,
    showUsersModal,
    participants,
    isSideView = false,
}) => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [startingMessageForId, setStartingMessageForId] = useState<string | null>(null);

    const filteredParticipants = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();
        if (!search) return participants;

        return participants.filter(user =>
            user.firstName?.toLowerCase().includes(search) ||
            user.lastName?.toLowerCase().includes(search) ||
            user.displayName?.toLowerCase().includes(search) ||
            user.email?.toLowerCase().includes(search) ||
            user.employeeRole?.toLowerCase().includes(search)
        );
    }, [participants, searchTerm]);

    useEffect(() => {
        if (!showUsersModal) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const unregisterEscapeLayer = registerChatEscapeLayer(handleCloseUsersModal);

        return () => {
            document.body.style.overflow = previousOverflow;
            unregisterEscapeLayer();
        };
    }, [showUsersModal, handleCloseUsersModal]);

    const openConversation = (conversationId: number) => {
        handleCloseUsersModal();
        if (isSideView) {
            currentConversationId$.next(conversationId);
            selectedConversation$.next(conversationId);
        } else {
            navigate(`/chat/${conversationId}`);
        }
    };

    const handleDirectMessage = async (targetUserId: string) => {
        if (!currentUser || targetUserId === currentUser.uid) return;

        setStartingMessageForId(targetUserId);
        try {
            const token = await currentUser.getIdToken();
            const res = await createConversation(token, [targetUserId]);
            const conversationId = res.data?.id;

            if (conversationId) {
                openConversation(conversationId);
            } else {
                toast.error(res.message || 'Failed to start conversation.');
            }
        } catch (error: any) {
            toast.error(error.message || 'An error occurred while starting the conversation.');
        } finally {
            setStartingMessageForId(null);
        }
    };

    if (!showUsersModal || typeof document === 'undefined') return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 px-4 py-8 backdrop-blur-[1px]"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) handleCloseUsersModal();
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="chat-members-title"
                className="chat-participant-dialog dark-surface dark-border-subtle flex max-h-[min(680px,calc(100vh-48px))] w-full max-w-[520px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.28)] dark:border-slate-700 dark:bg-[#171923]"
            >
                <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
                    <div className="min-w-0">
                        <h2 id="chat-members-title" className="m-0 text-[16px] font-semibold text-slate-900 dark:text-slate-100">
                            {participants.length === 2 ? 'Chat members' : 'Group members'}
                        </h2>
                        <p className="mb-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            {participants.length} {participants.length === 1 ? 'member' : 'members'} in this conversation
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleCloseUsersModal}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-none bg-transparent text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                        aria-label="Close members"
                    >
                        <X size={19} />
                    </button>
                </div>

                <div className="shrink-0 px-5 pb-3 pt-4">
                    <div className="dark-surface-muted dark-border-subtle flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3 transition-all focus-within:border-[#7E57C2] focus-within:bg-white focus-within:ring-4 focus-within:ring-[#7E57C2]/10 dark:border-slate-700 dark:bg-slate-800 dark:focus-within:border-purple-400 dark:focus-within:bg-slate-800 dark:focus-within:ring-purple-400/10">
                        <Search size={16} className="shrink-0 text-slate-400" />
                        <input
                            autoFocus
                            type="search"
                            placeholder="Search members..."
                            value={searchTerm}
                            onChange={event => setSearchTerm(event.target.value)}
                            className="w-full border-none bg-transparent py-2.5 text-sm text-slate-700 outline-none shadow-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
                        />
                    </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4 scrollbar-thin">
                    {filteredParticipants.length === 0 ? (
                        <div className="flex min-h-[220px] flex-col items-center justify-center px-6 text-center text-slate-500 dark:text-slate-400">
                            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800">
                                <Users size={20} />
                            </div>
                            <p className="m-0 text-sm font-semibold text-slate-700 dark:text-slate-200">No members found</p>
                            <p className="mb-0 mt-1 text-xs">Try a different name or email address.</p>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filteredParticipants.map(user => {
                                const isCurrentUser = user.id === currentUser?.uid;
                                const name = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Member';

                                return (
                                    <div key={user.id} className="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/80">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center">
                                            <AvatarImage userId={user.id} displayName={name} size="xs" alternate />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex min-w-0 items-center gap-1.5">
                                                <p className="m-0 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{name}</p>
                                                {isCurrentUser && (
                                                    <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-700 dark:text-slate-300">You</span>
                                                )}
                                            </div>
                                            <p className="m-0 mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
                                                {user.employeeRole || user.email || 'Conversation member'}
                                            </p>
                                        </div>
                                        {!isCurrentUser && (
                                            <button
                                                type="button"
                                                onClick={() => handleDirectMessage(user.id)}
                                                disabled={startingMessageForId === user.id}
                                                className="dark-surface-muted dark-border-subtle flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-purple-200 hover:bg-purple-50 hover:text-[#7E57C2] disabled:cursor-wait disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-purple-400/50 dark:hover:bg-purple-400/10 dark:hover:text-purple-300"
                                            >
                                                <MessageCircle size={14} />
                                                <span>{startingMessageForId === user.id ? 'Opening...' : 'Message'}</span>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ChatParticipantModal;
